// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import LoginForm from './Login/LoginForm/LoginForm';
import SignUpForm from './SignUp/SignUpForm/SignUpForm';
import Modal from '../../../elements/Modal/Modal';
import VerificationRemainderModalContainer
  from '../auth-modals/verification-remainder-modal/container/VerificationRemainderModalContainer';
import SuccessErrorModal from '../../../elements/SuccessErrorModal/SuccessErrorModal';
import RecoverUserModalContainer from '../auth-modals/forgot-password-modal/container/RecoverUserModalContainer';
import SendForgotPasswordModalContainer
  from '../auth-modals/forgot-password-modal/container/SendForgotPasswordModalContainer';
import AlreadyExistsFacebookAccountContainer
  from '../auth-modals/already-exists-facebook-account/container/AlreadyExistsFacebookAccountContainer';
import AlreadyExistsMailAccountContainer
  from '../auth-modals/already-exists-mail-account/container/AlreadyExistsMailAccountContainer';
import FacebookNeedMail
  from '../auth-modals/facebook-need-email/container/FacebookNeedMailContainer';

import type { Language, User } from '../../../entities';
import type { HTTPError } from '../../../error';
import type { LocaleState } from '../../locale/duck';

type Props = {
  +authModalVisible: boolean;
  +authModalShowLogin: boolean;
  +user?: User;
  +registerSuccessState?: boolean;
  +authenticated?: boolean;
  +fbLogin: (country: ?string, language: Language, token: string,
             email?: string, redirectTo?: string) => void;
  +login: (email: string, password: string, redirectTo?: string) => void;
  +logout: () => void;
  +isAuthing?: boolean;
  +authError?: HTTPError;
  +register: (country: ?string, firstName: string, lastName: string,
              email: string, password: string, language: string) => void;
  +isRegistering?: boolean;
  +signUpError?: HTTPError;
  +handleCloseAuthModal: () => void;
  +handleToggleAuthModal: () => void;
  +sendForgotPassword: () => void;
  +resetState: () => void;
  +verificationSuccess?: boolean;
  +locale: LocaleState;
  +sendForgotPasswordError?: HTTPError;
};

type State = {
  verificationReminderModalVisible: boolean;
  sendForgotPasswordModalVisible: boolean;
  alreadyExistsFacebookAccountModalVisible: boolean;
  alreadyExistsMailAccountModalVisible: boolean;
  facebookNeedEmailModalVisible: boolean;
};

export default class Auth extends Component<Props, State> {
  state = {
    verificationReminderModalVisible: false,
    sendForgotPasswordModalVisible: false,
    alreadyExistsFacebookAccountModalVisible: false,
    alreadyExistsMailAccountModalVisible: false,
    facebookNeedEmailModalVisible: false,
  };

  componentDidUpdate(prevProps: Props) {
    const { registerSuccessState, user, authError, signUpError, sendForgotPasswordError } = this.props;
    const { alreadyExistsFacebookAccountModalVisible, alreadyExistsMailAccountModalVisible } = this.state;

    if (user && prevProps.registerSuccessState !== registerSuccessState) {
      this.setState({
        verificationReminderModalVisible: true,
      });
    }

    if (authError && (prevProps.authError !== authError) && authError.code === 401 && authError.data) {
      if (authError.data.name === 'UserExistsWithMailError') {
        this.handleOpenAlreadyExistsMailAccountModal();
      }
    }

    if (sendForgotPasswordError && (prevProps.sendForgotPasswordError !== sendForgotPasswordError) &&
      sendForgotPasswordError.code === 401 && sendForgotPasswordError.data) {
      if (sendForgotPasswordError.data.name === 'UserExistsWithFacebookError') {
        this.handleOpenAlreadyExistsFacebookAccountModal();
      }
    }

    if (signUpError && (prevProps.signUpError !== signUpError) && signUpError.code === 400 && signUpError.data) {
      if (signUpError.data.name === 'UserAlreadyExistsError') {
        this.handleOpenAlreadyExistsMailAccountModal();
      }
    }

    if (authError && (prevProps.authError !== authError) && authError.code === 401 && authError.data) {
      if (authError.data.name === 'UserExistsWithFacebookError') {
        this.handleOpenAlreadyExistsFacebookAccountModal();
      }
    }

    if (authError && (prevProps.authError !== authError) && authError.code === 400 && authError.data) {
      if (authError.data.name === 'FacebookUserWithNoEmail') {
        this.handleOpenFacebookNeedModal();
      }
    }

    if (user && user !== prevProps.user && alreadyExistsFacebookAccountModalVisible) {
      this.setState({
        alreadyExistsFacebookAccountModalVisible: false,
      });
    }

    if (user && user !== prevProps.user && alreadyExistsMailAccountModalVisible) {
      this.setState({
        alreadyExistsMailAccountModalVisible: false,
      });
    }
  }

  handleOpenSentForgotPasswordModal() {
    const { handleCloseAuthModal } = this.props;
    this.setState({
      sendForgotPasswordModalVisible: true,
    });
    handleCloseAuthModal();
    this.handleCloseAlreadyExistsMailAccountModal();
  }

  handleCloseSentForgotPasswordModal() {
    this.setState({
      sendForgotPasswordModalVisible: false,
    });
  }

  handleOpenAlreadyExistsFacebookAccountModal() {
    const { handleCloseAuthModal } = this.props;
    this.setState({
      alreadyExistsFacebookAccountModalVisible: true,
    });
    this.handleCloseSentForgotPasswordModal();
    handleCloseAuthModal();
  }

  handleCloseAlreadyExistsFacebookAccountModal() {
    this.setState({
      alreadyExistsFacebookAccountModalVisible: false,
    });
  }

  handleOpenAlreadyExistsMailAccountModal() {
    const { handleCloseAuthModal } = this.props;
    this.setState({
      alreadyExistsMailAccountModalVisible: true,
    });
    handleCloseAuthModal();
  }

  handleCloseAlreadyExistsMailAccountModal() {
    this.setState({
      alreadyExistsMailAccountModalVisible: false,
    });
  }

  handleCloseVerificationReminderModal() {
    this.setState({
      verificationReminderModalVisible: false,
    });
  }

  handleOpenFacebookNeedModal() {
    const { handleCloseAuthModal } = this.props;
    this.setState({
      facebookNeedEmailModalVisible: true,
    });
    handleCloseAuthModal();
  }

  handleCloseFacebookNeedModal() {
    this.setState({
      facebookNeedEmailModalVisible: false,
    });
  }

  render() {
    const {
      authenticated, login, fbLogin, logout, isAuthing, authModalVisible,
      authError, register, isRegistering, signUpError, handleCloseAuthModal, locale,
      authModalShowLogin, handleToggleAuthModal, resetState, verificationSuccess, registerSuccessState,
    } = this.props;

    const {
      verificationReminderModalVisible,
      sendForgotPasswordModalVisible,
      alreadyExistsFacebookAccountModalVisible,
      alreadyExistsMailAccountModalVisible,
      facebookNeedEmailModalVisible,
    } = this.state;

    return <Fragment>
      <Modal key="modal"
             open={authModalVisible}
             handleClose={handleCloseAuthModal}
             width={544}
             height={authModalShowLogin ? 560 : 740}>
        <section style={{ display: authModalShowLogin ? 'block' : 'none' }}>
          <LoginForm login={login}
                     fbLogin={fbLogin}
                     logout={logout}
                     isAuthing={isAuthing}
                     authError={authError}
                     handleOpenSentForgotPasswordModal={this.handleOpenSentForgotPasswordModal.bind(this)}
                     handleClickSignUp={handleToggleAuthModal}
                     authenticated={authenticated}
                     handleClose={handleCloseAuthModal}
                     authModalVisible={authModalVisible}
                     locale={locale}
                     authModalShowLogin={authModalShowLogin}/>
        </section>
        <section style={{ display: authModalShowLogin ? 'none' : 'block' }}>
          <SignUpForm register={register}
                      isRegistering={isRegistering}
                      signUpError={signUpError}
                      fbLogin={fbLogin}
                      logout={logout}
                      isAuthing={isAuthing}
                      authError={authError}
                      handleClose={handleCloseAuthModal}
                      handleClickLogin={handleToggleAuthModal}
                      authenticated={authenticated}
                      authModalVisible={authModalVisible}
                      locale={locale}
                      registerSuccessState={registerSuccessState}
                      authModalShowLogin={authModalShowLogin}/>
        </section>
      </Modal>
      <VerificationRemainderModalContainer isOpen={verificationReminderModalVisible}
                                           handleClose={this.handleCloseVerificationReminderModal.bind(this)}/>
      <SendForgotPasswordModalContainer isOpen={sendForgotPasswordModalVisible}
                                        handleClose={this.handleCloseSentForgotPasswordModal.bind(this)}/>
      <AlreadyExistsFacebookAccountContainer isOpen={alreadyExistsFacebookAccountModalVisible}
                                             locale={locale}
                                             handleClose={this.handleCloseAlreadyExistsFacebookAccountModal.bind(this)}/>
      <AlreadyExistsMailAccountContainer isOpen={alreadyExistsMailAccountModalVisible}
                                         handleClose={this.handleCloseAlreadyExistsMailAccountModal.bind(this)}
                                         login={login}
                                         authError={authError}
                                         handleOpenSentForgotPasswordModal={this.handleOpenSentForgotPasswordModal.bind(this)}/>
      <RecoverUserModalContainer handleClose={resetState}/>
      <SuccessErrorModal isOpen={!!verificationSuccess}
                         handleCloseModal={() => resetState()}
                         titleInt="home.validate.success.title"
                         textInt="home.validate.success.subtitle"
                         color="lightBlue"
                         success={!!verificationSuccess}/>
      <FacebookNeedMail isOpen={facebookNeedEmailModalVisible}
                        locale={locale}
                        handleClose={this.handleCloseFacebookNeedModal.bind(this)}/>
    </Fragment>
  }
}

Auth.propTypes = {
  authModalVisible: PropTypes.bool,
  authModalShowLogin: PropTypes.bool,
  user: PropTypes.object,
  authenticated: PropTypes.bool,
  login: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  fbLogin: PropTypes.func.isRequired,
  isAuthing: PropTypes.bool,
  authError: PropTypes.object,
  register: PropTypes.func.isRequired,
  isRegistering: PropTypes.bool,
  signUpError: PropTypes.object,
  registerSuccessState: PropTypes.bool,
  handleCloseAuthModal: PropTypes.func.isRequired,
  handleToggleAuthModal: PropTypes.func.isRequired,
  sendForgotPassword: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  verificationSuccess: PropTypes.bool,
  locale: PropTypes.object,
  sendForgotPasswordError: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/modules/auth/components/Auth.jsx